import {
  Box,
  Typography,
  IconButton,
  Button,
  Card,
  CircularProgress,
  Divider,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import Swal from 'sweetalert2';
import config from '../../Config';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

const SHARED_STYLE = {
  base: {
    fontSize: '16px',
    color: '#1e1e1e',
    fontFamily: "'Roboto', sans-serif",
    '::placeholder': {
      color: '#b0b0b0',
    },
    iconColor: '#2e7d7b',
  },
  invalid: {
    color: '#e53935',
    iconColor: '#e53935',
  },
};

const ELEMENT_OPTIONS = {
  style: SHARED_STYLE,
  showIcon: false,
};

export default function OrderScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const stripe = useStripe();
  const elements = useElements();

  const [product, setProduct] = useState(null);
  const [isProductLoading, setIsProductLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  const [cardNumberError, setCardNumberError] = useState(null);
  const [cardExpiryError, setCardExpiryError] = useState(null);
  const [cardCvcError, setCardCvcError] = useState(null);
  const [paymentError, setPaymentError] = useState(null);

  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState("");

  const priceType = product?.priceType;
  const isHourly = priceType === 'hourly';
  const priceSuffix = isHourly ? '/ hr' : '/ per serving';

  useEffect(() => {
    const roomsFromState = state?.rooms;
    const roomsFromSession = (() => {
      try {
        return JSON.parse(sessionStorage.getItem('bookingRooms') || '[]');
      } catch {
        return [];
      }
    })();

    const resolvedRooms = roomsFromState?.length ? roomsFromState : roomsFromSession;
    setRooms(resolvedRooms);

    if (resolvedRooms.length > 0) {
      setSelectedRoomId(resolvedRooms[0].roomID);
    }
  }, [state]);

  useEffect(() => {
    if (!id) return;

    const propertyId = sessionStorage.getItem('propertyDocumentID');
    const token = localStorage.getItem('token');

    if (!propertyId || !token) {
      setIsProductLoading(false);
      return;
    }

    const url = `${config.apiBaseUrl}/guest/getUpsale/property/${propertyId}/upsale/${id}`;

    setIsProductLoading(true);

    fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch product details');
        return res.json();
      })
      .then((data) => {
        if (data?.success && data?.upsaleData) {
          const upsale = data.upsaleData;
          const prod = upsale.product;

          setProduct({
            _id: upsale._id,
            price: upsale.price,
            taxPercent: upsale.taxPercent,
            extraCharges: upsale.extraCharges,
            isActive: upsale.isActive,
            priceType: upsale.priceType,
            name: prod?.name,
            image: prod?.image,
            description: prod?.description,
          });
        } else {
          setProduct(null);
        }
      })
      .catch(() => {
        setProduct(null);
      })
      .finally(() => {
        setIsProductLoading(false);
      });
  }, [id]);

  const handleBackClick = () => navigate(-1);

  const itemPrice = product?.price || 0;
  const total = itemPrice * quantity;

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1) setQuantity(newQuantity);
  };

  const allFieldsComplete = cardNumberComplete && cardExpiryComplete && cardCvcComplete;

  const handlePlaceOrderClick = async () => {
    if (!product || !stripe || !elements || !allFieldsComplete) return;

    if (rooms.length > 0 && !selectedRoomId) {
      Swal.fire({
        icon: 'error',
        title: 'Select a room',
        text: 'Please select which room this order is for.',
      });
      return;
    }

    const reservationId = sessionStorage.getItem('bookingDocumentID');
    const propertyId = sessionStorage.getItem('propertyDocumentID');
    const token = localStorage.getItem('token');
    const email = localStorage.getItem('userEmail');

    if (!reservationId || !propertyId || !token) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Information',
        text: 'Missing reservation or property details.',
      });
      return;
    }

    setIsPlacingOrder(true);
    setPaymentError(null);

    try {
      // STEP 1: Create Payment Intent
      const paymentIntentRes = await fetch(`${config.apiBaseUrl}/payment-service/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: Math.round(total * 100),
          currency: 'gbp',
          email: email || undefined,
        }),
      });

      if (!paymentIntentRes.ok) {
        const errData = await paymentIntentRes.json();

        Swal.fire({
          icon: 'error',
          title: 'Payment Failed',
          text: errData.message || 'Failed to create payment intent',
        });

        throw new Error(errData.message || 'Failed to create payment intent');
      }

      const { clientSecret, paymentIntentId } = await paymentIntentRes.json();

      // STEP 2: Confirm Card Payment
      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) throw new Error('Card element not found');

      const { error, paymentIntent: confirmedPaymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: { card: cardNumberElement },
        }
      );

      if (error) throw new Error(error.message);
      if (confirmedPaymentIntent.status !== 'succeeded') {
        throw new Error('Payment did not succeed.');
      }

      // STEP 3: Place Order — includes roomId for the room the user selected
      const orderRes = await fetch(`${config.apiBaseUrl}/guest/placeOrder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reservationId,
          propertyId,
          productId: product._id,
          quantity,
          paymentId: paymentIntentId,
          roomId: selectedRoomId,
        }),
      });

      if (!orderRes.ok) {
        const errData = await orderRes.json();

        Swal.fire({
          icon: 'error',
          title: 'Order Failed',
          text: errData.message || 'Failed to place order',
        });

        throw new Error(errData.message || 'Failed to place order');
      }

      navigate('/order-success');
    } catch (err) {
      const message = err.message || 'Something went wrong';

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: message,
      });

      setPaymentError(message);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        maxWidth: 600,
        mx: 'auto',
        px: 3,
        py: 2,
        bgcolor: '#fefefe',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
        borderRadius: 3,
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', mb: 3 }}>
        <IconButton
          onClick={handleBackClick}
          sx={{ position: 'absolute', left: 0, color: '#2e7d7b' }}
          aria-label="go back"
          size="small"
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>

        <Typography
          variant="h6"
          align="center"
          sx={{
            flexGrow: 1,
            fontWeight: 700,
            color: '#2e7d7b',
            letterSpacing: 0.7,
          }}
        >
          Place Your Order
        </Typography>
      </Box>

      {isProductLoading ? (
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4, px: 1 }}>
            <Skeleton variant="circular" width={130} height={130} sx={{ flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="60%" height={32} sx={{ mb: 1 }} />
              <Skeleton width="40%" height={40} sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', width: 120 }}>
                <Skeleton
                  variant="rectangular"
                  width={28}
                  height={28}
                  sx={{ borderRadius: '50%' }}
                />
                <Skeleton width={40} height={28} sx={{ mx: 2, borderRadius: 1 }} />
                <Skeleton
                  variant="rectangular"
                  width={28}
                  height={28}
                  sx={{ borderRadius: '50%' }}
                />
              </Box>
            </Box>
          </Box>

          <Skeleton variant="rectangular" height={48} sx={{ mb: 3, borderRadius: 1 }} />
          <Skeleton width={120} height={20} sx={{ mb: 1 }} />

          <Skeleton variant="rectangular" height={44} sx={{ mb: 1.5, borderRadius: 1 }} />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Skeleton variant="rectangular" height={48} sx={{ flex: 1, borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={48} width={140} sx={{ borderRadius: 1 }} />
          </Box>

          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3, mb: 2 }} />

          <Skeleton variant="rectangular" height={46} sx={{ borderRadius: 30 }} />
        </Box>
      ) : product ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4, px: 1 }}>
            <Box
              sx={{
                width: 130,
                height: 130,
                borderRadius: '50%',
                backgroundImage: `url(${product.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                boxShadow: '0 4px 10px rgba(46,125,123,0.2)',
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                gutterBottom
                sx={{ fontSize: "16px", lineHeight: 1.3 }}
              >
                {product.name}
              </Typography>
              <Typography
                fontWeight={700}
                color="#2e7d7b"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}
              >
                £{itemPrice.toFixed(2)}
                <Typography
                  component="span"
                  color="textSecondary"
                  sx={{ fontSize: "12px", fontWeight: 500 }}
                >
                  {priceSuffix}
                </Typography>
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: '#2e7d7b',
                  borderRadius: '25px',
                  width: 'fit-content',
                  px: 0.5,
                  py: 0.3,
                  mt: 1,
                }}
              >
                <IconButton
                  onClick={() => handleQuantityChange(-1)}
                  sx={{ color: '#fff', width: 28, height: 28 }}
                >
                  <RemoveIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '18px', mx: 2 }}>
                  {quantity}
                </Typography>
                <IconButton
                  onClick={() => handleQuantityChange(1)}
                  sx={{ color: '#fff', width: 28, height: 28 }}
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, px: 1, lineHeight: 1.5 }}>
            {product.description}
          </Typography>

          {/* Room selector: dropdown if multiple rooms, plain readonly display if only one */}
          {rooms.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 1, color: '#2e7d7b' }}
              >
                Order For Room
              </Typography>

              {rooms.length > 1 ? (
                <FormControl fullWidth size="small">
                  <InputLabel id="room-select-label">Select Room</InputLabel>
                  <Select
                    labelId="room-select-label"
                    label="Select Room"
                    value={selectedRoomId}
                    onChange={(e) => setSelectedRoomId(e.target.value)}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: '#fafafa',
                    }}
                  >
                    {rooms.map((room) => (
                      <MenuItem key={room.roomID} value={room.roomID}>
                        {room.roomName}
                        {room.roomTypeName ? ` — ${room.roomTypeName}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    p: 1.25,
                    backgroundColor: '#fafafa',
                  }}
                >
                  <MeetingRoomOutlinedIcon sx={{ color: '#2e7d7b', fontSize: 20 }} />
                  <Typography variant="body1" fontWeight={600}>
                    {rooms[0].roomName}
                    {rooms[0].roomTypeName ? ` — ${rooms[0].roomTypeName}` : ''}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, color: '#2e7d7b' }}>
            Enter Card Details
          </Typography>

          <input
            type="text"
            name="fake-card-number"
            autoComplete="off"
            style={{ opacity: 0, height: 0, position: 'absolute', left: '-9999px' }}
            tabIndex={-1}
          />

          <Box
            sx={{
              border: '1px solid #ccc',
              borderRadius: 2,
              p: 1.25,
              mb: 2,
              backgroundColor: '#fafafa',
            }}
          >
            <Typography variant="caption" sx={{ color: '#666', mb: 0.5 }}>
              Card number
            </Typography>
            <CardNumberElement
              options={ELEMENT_OPTIONS}
              onChange={(e) => {
                setCardNumberComplete(!!e.complete);
                setCardNumberError(e.error ? e.error.message : null);
                if (paymentError) setPaymentError(null);
              }}
            />
            {cardNumberError && (
              <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                {cardNumberError}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box
              sx={{
                flex: 1,
                border: '1px solid #ccc',
                borderRadius: 2,
                p: 1.25,
                backgroundColor: '#fafafa',
              }}
            >
              <Typography variant="caption" sx={{ color: '#666', mb: 0.5 }}>
                Expiry
              </Typography>
              <CardExpiryElement
                options={ELEMENT_OPTIONS}
                onChange={(e) => {
                  setCardExpiryComplete(!!e.complete);
                  setCardExpiryError(e.error ? e.error.message : null);
                  if (paymentError) setPaymentError(null);
                }}
              />
              {cardExpiryError && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                  {cardExpiryError}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                width: 140,
                border: '1px solid #ccc',
                borderRadius: 2,
                p: 1.25,
                backgroundColor: '#fafafa',
              }}
            >
              <Typography variant="caption" sx={{ color: '#666', mb: 0.5 }}>
                CVV
              </Typography>
              <CardCvcElement
                options={{ ...ELEMENT_OPTIONS, placeholder: '123' }}
                onChange={(e) => {
                  setCardCvcComplete(!!e.complete);
                  setCardCvcError(e.error ? e.error.message : null);
                  if (paymentError) setPaymentError(null);
                }}
              />
              {cardCvcError && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                  {cardCvcError}
                </Typography>
              )}
            </Box>
          </Box>

          <Card
            elevation={3}
            sx={{
              borderRadius: 3,
              p: 3,
              mb: 3,
              backgroundColor: '#e0f2f1',
              border: '1px solid #b2dfdb',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" fontWeight={700} color="#004d40">
                Total
              </Typography>
              <Typography
                fontWeight={700}
                color="#004d40"
                sx={{ fontSize: "16px", whiteSpace: "nowrap" }}
              >
                £{total.toFixed(2)}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2, borderColor: '#80cbc4' }} />
            <Button
              fullWidth
              variant="contained"
              sx={{
                backgroundColor: '#00796b',
                color: 'white',
                py: 1.8,
                borderRadius: '30px',
                fontSize: '18px',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#004d40' },
              }}
              onClick={handlePlaceOrderClick}
              disabled={
                !product ||
                isPlacingOrder ||
                !allFieldsComplete ||
                (rooms.length > 0 && !selectedRoomId)
              }
            >
              {isPlacingOrder ? (
                <CircularProgress size={24} sx={{ color: '#fff' }} />
              ) : (
                'Place Your Order'
              )}
            </Button>
          </Card>
        </>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            px: 3,
          }}
        >
          <Inventory2OutlinedIcon sx={{ fontSize: 100, color: '#cfd8dc', mb: 2 }} />

          <Typography variant="subtitle1" fontWeight={600}>
            Product not available
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This item is currently unavailable. Please try another option.
          </Typography>

          <Button variant="outlined" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Box>
      )}
    </Box>
  );
}